/**
 * A critically damped spring: it reaches its target as fast as it can without overshooting, and it can be
 * sent somewhere else at any moment without a jolt, because it keeps its velocity. Every movement in the
 * atlas goes through one of these, which is what makes turning around mid-dive smooth.
 */
export class Spring {
  value: number;
  target: number;
  velocity = 0;
  /** Seconds to cover half the remaining distance. */
  halfLife: number;

  constructor(value: number, halfLife: number) {
    this.value = value;
    this.target = value;
    this.halfLife = halfLife;
  }

  /** Moves the spring on by dt seconds. Returns whether it is still moving. */
  step(dt: number, epsilon = 1e-5): boolean {
    if (this.settled(epsilon)) {
      this.value = this.target;
      this.velocity = 0;
      return false;
    }
    const y = (2 * Math.LN2) / Math.max(1e-4, this.halfLife);
    const j0 = this.value - this.target;
    const j1 = this.velocity + j0 * y;
    const e = Math.exp(-y * dt);
    this.value = e * (j0 + j1 * dt) + this.target;
    this.velocity = e * (this.velocity - j1 * y * dt);
    return true;
  }

  settled(epsilon = 1e-5): boolean {
    return Math.abs(this.value - this.target) < epsilon && Math.abs(this.velocity) < epsilon * 10;
  }

  /** Jumps straight to a value, as when following a finger. */
  set(value: number): void {
    this.value = this.target = value;
    this.velocity = 0;
  }
}
